import React, { useState, useEffect, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { getUserScopes, setScopes } from "./accessManagerApi";
import {
  getUserDepartments,
  listDepartments,
  extractDepartments,
  assignUserToDepartment,
  removeUserFromDepartment,
} from "./departmentsApi";
import {
  resolveEffectiveScopeList,
  getInheritedDepartmentScopes,
  getDepartmentScopeSources,
  formatScopeSourceLabel,
} from "./departmentScopes";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";
import ScopeTree from "./ScopeTree";
import "./AccessManagerPanel.css";

/**
 * EditUserDialog — modal dialog for modifying a single user's scopes.
 *
 * Fetches the user's current scopes on open, renders a ScopeTree for
 * editing, and includes a required notes field. Calls setScopes API on save.
 *
 * Extended with department membership management: shows the user's departments
 * as chips with remove controls, an add control for assigning new departments,
 * and an effective access list with source attribution.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {{ email: string, scopes: string[], granted_by: string|null }} props.user - The user to edit.
 * @param {() => void} props.onClose - Called when the dialog should close.
 * @param {(email: string, scopes: string[], notes: string) => Promise<void>} props.onSave - Called on successful save.
 */
export default function EditUserDialog({ open, user, onClose, onSave }) {
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingScopes, setFetchingScopes] = useState(false);
  const [error, setError] = useState(null);

  // Department state
  const [userDepartments, setUserDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState(null);
  const [pendingDepartmentId, setPendingDepartmentId] = useState(null);

  // Fetch user's current scopes when dialog opens
  const fetchUserScopes = useCallback(async (email, fallbackScopes) => {
    setFetchingScopes(true);
    setError(null);
    try {
      const response = await getUserScopes(email);
      const data = response.data;
      // Handle response shapes: array, { scopes: [] }, or use fallback
      const scopes = Array.isArray(data)
        ? data
        : data?.scopes ?? fallbackScopes ?? [];
      setSelectedScopes(scopes);
    } catch (err) {
      // Fallback to user's scopes from props if fetch fails
      setSelectedScopes(fallbackScopes || []);
    } finally {
      setFetchingScopes(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !user) return;

    let cancelled = false;

    async function fetchScopes() {
      setFetchingScopes(true);
      setError(null);
      try {
        const response = await getUserScopes(user.email);
        if (!cancelled) {
          const data = response.data;
          const scopes = Array.isArray(data)
            ? data
            : data?.scopes ?? user.scopes ?? [];
          setSelectedScopes(scopes);
        }
      } catch (err) {
        if (!cancelled) {
          setSelectedScopes(user.scopes || []);
        }
      } finally {
        if (!cancelled) {
          setFetchingScopes(false);
        }
      }
    }

    fetchScopes();

    return () => {
      cancelled = true;
    };
  }, [open, user]);

  // Fetch departments on open (independent of scope fetch)
  const fetchDepartments = useCallback(async (email) => {
    setDeptLoading(true);
    setDeptError(null);
    try {
      const [userDeptRes, allDeptRes] = await Promise.all([
        getUserDepartments(email),
        listDepartments(),
      ]);
      setUserDepartments(extractDepartments(userDeptRes.data));
      setAllDepartments(extractDepartments(allDeptRes.data));
    } catch (err) {
      setDeptError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setDeptLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !user) return;

    let cancelled = false;

    (async () => {
      setDeptLoading(true);
      setDeptError(null);
      try {
        const [userDeptRes, allDeptRes] = await Promise.all([
          getUserDepartments(user.email),
          listDepartments(),
        ]);
        if (!cancelled) {
          setUserDepartments(extractDepartments(userDeptRes.data));
          setAllDepartments(extractDepartments(allDeptRes.data));
        }
      } catch (err) {
        if (!cancelled) {
          setDeptError(formatErrorMessage(normalizeApiError(err)));
        }
      } finally {
        if (!cancelled) {
          setDeptLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setNotes("");
      setError(null);
      setSelectedScopes([]);
      setUserDepartments([]);
      setAllDepartments([]);
      setDeptLoading(false);
      setDeptError(null);
      setPendingDepartmentId(null);
    }
  }, [open]);

  const handleAssignDepartment = async (departmentId) => {
    if (!user || pendingDepartmentId !== null) return;

    const id = Number(departmentId);
    const department = allDepartments.find((d) => d.id === id);
    if (!department) return;

    setPendingDepartmentId(id);
    setDeptError(null);

    // Optimistic add
    const previousDepartments = userDepartments;
    setUserDepartments((prev) => [...prev, department]);

    try {
      await assignUserToDepartment(user.email, id);
    } catch (err) {
      // Rollback
      setUserDepartments(previousDepartments);
      setDeptError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setPendingDepartmentId(null);
    }
  };

  const handleRemoveDepartment = async (departmentId) => {
    if (!user || pendingDepartmentId !== null) return;

    setPendingDepartmentId(departmentId);
    setDeptError(null);

    // Optimistic remove
    const previousDepartments = userDepartments;
    setUserDepartments((prev) => prev.filter((d) => d.id !== departmentId));

    try {
      await removeUserFromDepartment(user.email, departmentId);
      // Refetch direct scopes so re-enabled controls reflect server state (Req 9.4)
      await fetchUserScopes(user.email, user.scopes);
    } catch (err) {
      // Rollback
      setUserDepartments(previousDepartments);
      setDeptError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setPendingDepartmentId(null);
    }
  };

  const handleRetryDepartments = () => {
    if (user) {
      fetchDepartments(user.email);
    }
  };

  const handleSave = async () => {
    if (!notes.trim()) {
      setError("Notes are required.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await setScopes(user.email, selectedScopes, notes.trim());
      if (onSave) {
        await onSave(user.email, selectedScopes, notes.trim());
      }
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save scope changes.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  // Derive department-inherited scopes and sources
  const inheritedScopes = getInheritedDepartmentScopes(userDepartments);
  const inheritedSources = getDepartmentScopeSources(userDepartments);
  const effectiveList = resolveEffectiveScopeList(selectedScopes, userDepartments);

  // Departments the user does not currently belong to
  const userDepartmentIds = new Set(userDepartments.map((d) => d.id));
  const availableDepartments = allDepartments.filter((d) => !userDepartmentIds.has(d.id));

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-dialog__overlay" />
        <Dialog.Content className="access-manager-dialog__content">
          <Dialog.Title className="access-manager-dialog__title">
            Edit Scopes
          </Dialog.Title>
          <Dialog.Description className="access-manager-dialog__description">
            Modify access scopes for <strong>{user.email}</strong>
          </Dialog.Description>

          {/* Departments section */}
          <div className="user-departments__section">
            <h3 className="user-departments__section-title">Departments</h3>

            {deptLoading && (
              <div className="user-departments__loading">Loading departments…</div>
            )}

            {!deptLoading && deptError && (
              <div className="user-departments__error" role="alert">
                {deptError}
                <button
                  type="button"
                  className="user-departments__retry-btn"
                  onClick={handleRetryDepartments}
                >
                  Retry
                </button>
              </div>
            )}

            {!deptLoading && !deptError && (
              <>
                <div className="user-departments__chips">
                  {userDepartments.map((dept) => (
                    <span key={dept.id} className="user-departments__chip">
                      {dept.name}
                      <button
                        type="button"
                        className="user-departments__chip-remove"
                        onClick={() => handleRemoveDepartment(dept.id)}
                        disabled={pendingDepartmentId === dept.id}
                        aria-label={`Remove ${dept.name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <div className="user-departments__add">
                  <label htmlFor="add-department-select" className="user-departments__add-label">
                    Add to department
                  </label>
                  <select
                    id="add-department-select"
                    className="user-departments__add-select"
                    value=""
                    onChange={(e) => handleAssignDepartment(e.target.value)}
                    disabled={pendingDepartmentId !== null || availableDepartments.length === 0}
                  >
                    <option value="" disabled>
                      {availableDepartments.length === 0
                        ? "No departments available"
                        : "Select a department…"}
                    </option>
                    {availableDepartments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Effective access list */}
          {!deptLoading && !deptError && effectiveList.length > 0 && (
            <div className="effective-scopes__section">
              <h3 className="effective-scopes__title">Effective Access</h3>
              <ul className="effective-scopes__list">
                {effectiveList.map((entry) => (
                  <li key={entry.scope} className="effective-scopes__item">
                    <span className="effective-scopes__scope">{entry.scope}</span>
                    <span className="effective-scopes__source">{entry.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Scope tree */}
          {fetchingScopes ? (
            <div className="access-manager-panel__loading">
              Loading scopes…
            </div>
          ) : (
            <>
              {inheritedScopes.length > 0 && (
                <p className="user-departments__guidance">
                  Inherited scopes are removed by removing the user from the granting department.
                </p>
              )}
              <ScopeTree
                selectedScopes={selectedScopes}
                onChange={setSelectedScopes}
                disabled={loading}
                inheritedScopes={inheritedScopes}
                inheritedSources={inheritedSources}
              />
            </>
          )}

          <div className="access-manager-dialog__notes">
            <label
              htmlFor="edit-user-notes"
              className="access-manager-dialog__notes-label"
            >
              Notes <span aria-hidden="true">*</span>
            </label>
            <textarea
              id="edit-user-notes"
              className="access-manager-dialog__notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for this change (required)"
              required
              disabled={loading}
              rows={3}
            />
          </div>

          {error && (
            <div className="access-manager-panel__error" role="alert">
              {error}
            </div>
          )}

          <div className="access-manager-dialog__actions">
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-dialog__btn access-manager-dialog__btn--cancel"
                disabled={loading}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="access-manager-dialog__btn access-manager-dialog__btn--confirm"
              onClick={handleSave}
              disabled={loading || fetchingScopes}
            >
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
